import type { ReactNode } from 'react';
import { Alert, Button, Empty, Result, Skeleton, Space, Typography } from 'antd';
import { errorMessage } from './errorMessage';

/**
 * One loading / error / empty treatment for async surfaces.
 *
 * Replaces ten bare unpositioned `<Spin/>` bodies and five divergent error
 * idioms — including one that rendered "404 — not found" for 403s, 500s, and
 * network failures alike. Errors here never claim a status they do not know.
 *
 * `errorPresentation='inline'` (default) renders an Alert for a body that can
 * hold it; `'page'` renders a full-page Result, suited to a surface with no
 * other body content. `skeleton='form'` yields a form-sized skeleton for
 * detail pages; `'table'` (default) for tables; `'none'` renders nothing.
 *
 * Precedence is error > loading > empty > children.
 */
export interface AsyncBoundaryProps {
  loading?: boolean;
  error?: unknown;
  /** True when the request succeeded but returned nothing. */
  empty?: boolean;
  skeleton?: 'table' | 'form' | 'none';
  emptyTitle?: string;
  emptyDescription?: ReactNode;
  emptyAction?: ReactNode;
  /** 'inline' (default) → Alert; 'page' → full-page Result for a surface with no body. */
  errorPresentation?: 'inline' | 'page';
  onRetry?: () => void;
  children: ReactNode;
}

export function AsyncBoundary({
  loading,
  error,
  empty,
  skeleton = 'table',
  emptyTitle,
  emptyDescription,
  emptyAction,
  errorPresentation = 'inline',
  onRetry,
  children,
}: AsyncBoundaryProps) {
  if (error) {
    const message = errorMessage(error, 'Something went wrong.');
    if (errorPresentation === 'page') {
      return (
        <Result
          status="error"
          title={message}
          extra={
            onRetry ? (
              <Button onClick={onRetry}>Retry</Button>
            ) : undefined
          }
        />
      );
    }
    return (
      <Alert
        type="error"
        showIcon
        message={message}
        action={
          onRetry ? (
            <Button size="small" onClick={onRetry}>
              Retry
            </Button>
          ) : undefined
        }
      />
    );
  }

  if (loading) {
    if (skeleton === 'none') return null;
    if (skeleton === 'form') {
      return (
        <Skeleton
          active
          title={false}
          paragraph={{ rows: 8, width: ['30%', '90%', '30%', '90%', '30%', '90%', '20%', '40%'] }}
        />
      );
    }
    return <Skeleton active paragraph={{ rows: 6 }} title={false} />;
  }

  if (empty) {
    const description =
      emptyTitle || emptyDescription ? (
        <Space direction="vertical" size={4}>
          {emptyTitle && <Typography.Text strong>{emptyTitle}</Typography.Text>}
          {emptyDescription && (
            <Typography.Text type="secondary">{emptyDescription}</Typography.Text>
          )}
        </Space>
      ) : undefined;

    return (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={description}>
        {emptyAction}
      </Empty>
    );
  }

  return <>{children}</>;
}
